/**
 * Tournament overview showing group and bracket stages.
 */

import { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { getCountryByCode } from '@/data/countries';
import { getRoundName } from '@/lib/tournament';
import { cn } from '@/lib/utils';
import { useTournament } from './TournamentProvider';

export function TournamentOverview() {
  const { state, progress } = useTournament();

  if (!state) return null;

  return (
    <ScrollArea className="h-full">
      <div className="space-y-4 p-1">
        {/* Group Stage Overview */}
        {state.groups.length > 0 && (
          <div>
            <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
              <span>👥</span>
              Group Stage - Round {state.groupRound}
            </h3>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
              {state.groups.map((group, idx) => {
                const isCurrentGroup =
                  idx === state.currentGroupIndex &&
                  state.status === 'group_stage';
                const isCompleted = group.rankings !== null;

                return (
                  <div
                    key={group.id}
                    className={cn(
                      'rounded-lg border p-2 text-xs',
                      isCurrentGroup && 'border-primary bg-primary/5',
                      isCompleted && !isCurrentGroup && 'bg-muted/50',
                      !isCompleted && !isCurrentGroup && 'opacity-60',
                    )}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="font-medium">Group {idx + 1}</span>
                      {isCurrentGroup && (
                        <Badge
                          variant="default"
                          className="text-[10px] px-1 py-0"
                        >
                          Current
                        </Badge>
                      )}
                      {isCompleted && !isCurrentGroup && (
                        <span className="text-green-600">✓</span>
                      )}
                    </div>
                    <div className="space-y-0.5">
                      {(group.rankings ?? group.countries).map((code, pos) => {
                        const country = getCountryByCode(code);
                        const willAdvance = pos < 2;
                        return (
                          <div
                            key={code}
                            className={cn(
                              'flex items-center gap-1 py-0.5 px-1 rounded',
                              isCompleted && willAdvance && 'bg-green-100',
                              isCompleted &&
                                !willAdvance &&
                                'bg-red-50 opacity-60',
                            )}
                          >
                            <span className="text-base">{country?.flag}</span>
                            <span className="truncate flex-1">
                              {country?.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bracket Stage Overview */}
        {state.bracket && state.bracket.length > 0 && <BracketOverview />}

        {/* Eliminated Countries */}
        {state.eliminatedInGroups.length > 0 && (
          <div>
            <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
              <span>❌</span>
              Eliminated ({state.eliminatedInGroups.length})
            </h3>
            <div className="flex flex-wrap gap-1">
              {state.eliminatedInGroups.slice(0, 20).map(({ code }) => {
                const country = getCountryByCode(code);
                return (
                  <span
                    key={code}
                    className="text-lg opacity-50"
                    title={country?.name}
                  >
                    {country?.flag}
                  </span>
                );
              })}
              {state.eliminatedInGroups.length > 20 && (
                <span className="text-xs text-muted-foreground">
                  +{state.eliminatedInGroups.length - 20} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

function BracketOverview() {
  const { state } = useTournament();

  const bracketByRound = useMemo(() => {
    if (!state?.bracket) return new Map();

    const byRound = new Map<number, typeof state.bracket>();
    for (const match of state.bracket) {
      if (!byRound.has(match.round)) {
        byRound.set(match.round, []);
      }
      byRound.get(match.round)!.push(match);
    }
    return byRound;
  }, [state?.bracket]);

  const totalRounds = Math.max(...Array.from(bracketByRound.keys()), 0);

  if (!state?.bracket) return null;

  return (
    <div>
      <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
        <span>⚔️</span>
        Knockout Stage
      </h3>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {Array.from(bracketByRound.entries())
          .sort(([a], [b]) => a - b)
          .map(([round, matches]) => (
            <div key={round} className="shrink-0 min-w-[120px]">
              <div className="text-xs font-medium text-muted-foreground mb-1.5 text-center">
                {getRoundName(round, totalRounds)}
              </div>
              <div className="space-y-2">
                {matches.map((match) => {
                  const countryA = getCountryByCode(match.countryA);
                  const countryB = getCountryByCode(match.countryB);
                  const isCurrent =
                    state.status === 'bracket_stage' &&
                    state.bracket?.[state.currentMatchIndex]?.id === match.id;

                  return (
                    <div
                      key={match.id}
                      className={cn(
                        'border rounded text-xs',
                        isCurrent && 'border-primary ring-1 ring-primary',
                        match.winner && 'bg-muted/30',
                      )}
                    >
                      <div
                        className={cn(
                          'flex items-center gap-1 px-1.5 py-1 border-b',
                          match.winner === match.countryA && 'bg-green-100',
                        )}
                      >
                        <span>{countryA?.flag}</span>
                        <span className="truncate flex-1">
                          {countryA?.name}
                        </span>
                        {match.winner === match.countryA && (
                          <span className="text-green-600">✓</span>
                        )}
                      </div>
                      <div
                        className={cn(
                          'flex items-center gap-1 px-1.5 py-1',
                          match.winner === match.countryB && 'bg-green-100',
                        )}
                      >
                        <span>{countryB?.flag}</span>
                        <span className="truncate flex-1">
                          {countryB?.name}
                        </span>
                        {match.winner === match.countryB && (
                          <span className="text-green-600">✓</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
